import { useFeatureIsOn } from 'growthbook'
import { User01Icon } from '@repo/studio-design-system-ui-icons/users/user-01'
import { Loading01Icon } from '@repo/studio-design-system-ui-icons/general/loading-01'
import * as React from 'react'
import { AuthButton } from '../../auth/auth-button'
import { useIsLoggedInQuery, useUserQuery } from 'auth/hooks'
import { useUserAvatarQuery } from '@repo/studio-hooks-auth/use-user-avatar'
import { useMyAvatarUrl } from '@repo/cf-user/hooks/use-my-avatar-url'
import * as Avatar from 'primitives/avatar'
import { tw } from 'utils/classnames'
import { ROUTES } from '@repo/studio-utils/routes'
import { useStudioConfig, useStudioTranslate } from '@repo/studio-config/studio-config-context'
import { StudioLayoutLink } from '@repo/studio-config/studio-config-router'

const WorkspaceSwitcher = React.lazy(() =>
  import('@repo/studio-components-workspace-switcher').then(module => ({
    default: module.WorkspaceSwitcher,
  })),
)
export function Auth({ fallback = <AuthButton /> }: { fallback?: React.ReactNode }) {
  const t = useStudioTranslate()
  const teamWorkspacesEnabled = useFeatureIsOn('studio-team-workspaces')
  const userAvatarQuery = useUserAvatarQuery()
  const userAvatarData = userAvatarQuery.data
  const isLoggedInQuery = useIsLoggedInQuery()
  const userSlugQuery = useUserQuery(undefined, {
    select: data => data.me?.user?.slug,
  })
  const isNsfwPlatform = useStudioConfig()?.isNsfwPlatform ?? false

  if (isLoggedInQuery.isPending || userAvatarQuery.isPending || userSlugQuery.isPending) {
    return <Avatar.Skeleton size="medium" />
  }

  if (!isLoggedInQuery.data) {
    return fallback
  }

  if (!isNsfwPlatform) {
    const avatar = (
      <UserAvatar
        initials={userAvatarData?.initials}
        hasImage={userAvatarData?.hasImage}
        avatarUrl={userAvatarData?.avatarUrl}
      />
    )
    const profilePath = userSlugQuery.data ? ROUTES.CREATOR.CREATOR(userSlugQuery.data) : undefined

    if (!teamWorkspacesEnabled || !process.env.VITE_RPC_TEAMS_URL) {
      return profilePath ? (
        <StudioLayoutLink
          to={profilePath}
          className="select-none"
          aria-label={t('studio.workspace_switcher.view_profile')}
        >
          {avatar}
        </StudioLayoutLink>
      ) : (
        avatar
      )
    }

    return (
      <React.Suspense
        fallback={
          profilePath ? (
            <StudioLayoutLink
              to={profilePath}
              className="select-none"
              aria-label={t('studio.workspace_switcher.view_profile')}
            >
              {avatar}
            </StudioLayoutLink>
          ) : (
            avatar
          )
        }
      >
        <WorkspaceSwitcher profilePath={profilePath}>{avatar}</WorkspaceSwitcher>
      </React.Suspense>
    )
  }

  return (
    <UserAvatar
      initials={userAvatarData?.initials}
      hasImage={userAvatarData?.hasImage}
      avatarUrl={userAvatarData?.avatarUrl}
    />
  )
}

function UserAvatar(props: { initials?: string; hasImage?: boolean; avatarUrl?: string | null }) {
  const t = useStudioTranslate()
  const { src: sizedAvatarUrl } = useMyAvatarUrl(props.avatarUrl, 32)
  return (
    <>
      <Avatar.Root size="medium">
        {props?.hasImage ? (
          <Avatar.Image
            src={sizedAvatarUrl || '/images/astro-avatar.png'}
            alt={t('studio.workspace_switcher.avatar')}
            className={tw(props.avatarUrl ? 'w-[32px]' : 'h-auto w-[28px] translate-y-[2px]')}
          />
        ) : null}
        <Avatar.Fallback delayMs={props?.hasImage ? 1000 : 0}>
          {props?.initials || '👩‍🚀'}
        </Avatar.Fallback>
      </Avatar.Root>
    </>
  )
}

export function MyAvatar() {
  const { data: isLoggedIn, isPending: isLoggedInPending } = useIsLoggedInQuery()
  const { data: userAvatarData, isPending: isUserAvatarPending } = useUserAvatarQuery()

  if (isLoggedInPending || (isLoggedIn && isUserAvatarPending)) {
    return (
      <Avatar.Root size="medium">
        <Avatar.Fallback delayMs={0}>
          <Loading01Icon className="size-4 text-service-white animate-spin" />
        </Avatar.Fallback>
      </Avatar.Root>
    )
  }

  if (!isLoggedIn) {
    return (
      <Avatar.Root size="medium">
        <Avatar.Fallback delayMs={0}>
          <User01Icon className="size-4 text-service-white" />
        </Avatar.Fallback>
      </Avatar.Root>
    )
  }

  return (
    <UserAvatar
      initials={userAvatarData?.initials}
      hasImage={userAvatarData?.hasImage}
      avatarUrl={userAvatarData?.avatarUrl}
    />
  )
}
